import React from "react";
import { TrendingUp, Eye, Pause, ShieldAlert, Minus, Star, ArrowUp, TrendingDown } from "lucide-react";

/**
 * SignalBadge — bold, screener-grade colour mapping.
 *
 *   STRONG BUY → dark green bg, white text, ★ star
 *   BUY        → teal/green bg, white text, ▲ arrow up
 *   WATCH      → amber bg, dark text
 *   HOLD       → grey bg, dark text
 *   AVOID      → red bg, white text
 *   NO_DATA    → muted, italic
 *
 * Also accepts TradingView-style values so the TV Signal badge on the
 * screener + Technical Analysis card renders IDENTICALLY to the AI
 * Signal and Deep AI Signal badges (same colour family, same pill
 * shape, same size tokens). Mapping:
 *
 *   STRONG BUY   → STRONG BUY (dark green)
 *   BUY          → BUY (teal)
 *   NEUTRAL      → HOLD colour (grey) — "wait and see"
 *   SELL         → WATCH colour (amber) — "caution / risk"
 *   STRONG SELL  → AVOID colour (red) — "clear downside"
 *
 * Sizes (uniform widths keep tables aligned across labels):
 *   sm  →  text-[10px], min-w-[80px]   (compact lists / tables)
 *   md  →  text-xs,     min-w-[96px]   (default)
 *   lg  →  text-sm,     min-w-[112px]  (hero callouts)
 *
 * Pass `uniform={false}` to opt out of the fixed width (useful in very
 * tight mobile grid cells where the pill must hug the label).
 */
const MAP = {
  STRONG_BUY: {
    // Dark forest green — high contrast, draws the eye
    cls: "bg-[hsl(142_72%_29%)] text-white border-[hsl(142_72%_29%)] font-semibold",
    Icon: Star,
    label: "STRONG BUY",
  },
  "STRONG BUY": {
    cls: "bg-[hsl(142_72%_29%)] text-white border-[hsl(142_72%_29%)] font-semibold",
    Icon: Star,
    label: "STRONG BUY",
  },
  BUY: {
    // Teal — solid bg, white text
    cls: "bg-[hsl(173_80%_30%)] text-white border-[hsl(173_80%_30%)] font-medium",
    Icon: ArrowUp,
    label: "BUY",
  },
  WATCH: {
    // Amber — dark text on amber bg
    cls: "bg-[hsl(38_92%_55%)] text-[hsl(20_50%_15%)] border-[hsl(38_92%_50%)] font-medium",
    Icon: Eye,
    label: "WATCH",
  },
  HOLD: {
    // Grey — neutral
    cls: "bg-[hsl(var(--muted))] text-foreground border-border font-medium",
    Icon: Pause,
    label: "HOLD",
  },
  AVOID: {
    // Red — solid bg, white text
    cls: "bg-[hsl(0_72%_45%)] text-white border-[hsl(0_72%_45%)] font-medium",
    Icon: ShieldAlert,
    label: "AVOID",
  },
  // ---- TradingView-style values (Technical Analysis, TV Signal col) ----
  // Same colour tokens as the AI-signal equivalents so screenshots of
  // all three badges side-by-side are indistinguishable in style. Only
  // the label + icon differ so users can still tell TV vs AI apart.
  NEUTRAL: {
    cls: "bg-[hsl(var(--muted))] text-foreground border-border font-medium",
    Icon: Pause,
    label: "NEUTRAL",
  },
  SELL: {
    cls: "bg-[hsl(38_92%_55%)] text-[hsl(20_50%_15%)] border-[hsl(38_92%_50%)] font-medium",
    Icon: TrendingDown,
    label: "SELL",
  },
  STRONG_SELL: {
    cls: "bg-[hsl(0_72%_45%)] text-white border-[hsl(0_72%_45%)] font-semibold",
    Icon: ShieldAlert,
    label: "STRONG SELL",
  },
  "STRONG SELL": {
    cls: "bg-[hsl(0_72%_45%)] text-white border-[hsl(0_72%_45%)] font-semibold",
    Icon: ShieldAlert,
    label: "STRONG SELL",
  },
  NO_DATA: {
    cls: "bg-muted/50 text-muted-foreground border-border italic",
    Icon: Minus,
    label: "—",
  },
};

// Each size ships with a min-width token so every badge — "BUY",
// "STRONG BUY", "HOLD", "AVOID" — renders at the same physical pixel
// width. This keeps tables visually aligned even when labels differ in
// character count. Content is centred inside the pill via
// `justify-center`.
const SIZE = {
  sm: "text-[10px] px-2 py-0.5 min-w-[80px]",
  md: "text-xs px-2.5 py-0.5 min-w-[96px]",
  lg: "text-sm px-3 py-1 min-w-[112px]",
};

const SignalBadge = ({
  signal,
  className = "",
  size = "md",
  showIcon = true,
  uniform = true,
}) => {
  const norm = (signal || "").toString().toUpperCase().replace(/[\s-]/g, "_");
  const key = MAP[norm] ? norm : (signal && MAP[signal] ? signal : "NO_DATA");
  const meta = MAP[key];
  const Icon = meta.Icon;
  const label = key === "NO_DATA" ? "—" : (meta.label || key);
  const sizeCls = uniform ? (SIZE[size] || SIZE.md) : (
    size === "sm" ? "text-[10px] px-1.5 py-0.5"
    : size === "lg" ? "text-sm px-2.5 py-1"
    : "text-xs px-2 py-0.5"
  );
  return (
    <span
      data-testid="signal-badge"
      title={key === "NO_DATA" ? "No signal yet — not enough data" : (meta.label || key)}
      className={`inline-flex items-center justify-center gap-1 rounded-full border whitespace-nowrap ${sizeCls} ${meta.cls} ${className}`}
    >
      {showIcon && <Icon className="h-3 w-3 shrink-0" />}
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// ScoreText — colour-codes the composite score number to match SignalBadge
// ---------------------------------------------------------------------------
//   ≥ 80  → dark green
//   65–79 → teal
//   40–64 → amber
//   < 40  → red
// ---------------------------------------------------------------------------
export const ScoreText = ({ score, className = "" }) => {
  if (score == null || isNaN(score)) {
    return <span className={`text-muted-foreground ${className}`}>—</span>;
  }
  const n = Math.round(score);
  const tone =
    n >= 80
      ? "text-[hsl(142_72%_29%)]"
      : n >= 65
        ? "text-[hsl(173_80%_30%)]"
        : n >= 40
          ? "text-[hsl(38_92%_45%)]"
          : "text-[hsl(0_72%_45%)]";
  return <span className={`font-semibold tabular-nums ${tone} ${className}`}>{n}/100</span>;
};

export default SignalBadge;
